import { Link } from 'react-router-dom';
import { Button, Menu, ActionIcon, Loader } from '@mantine/core';
import { IconTrash, IconExternalLink, IconFlagCog, IconDownload, IconBusFilled, IconNotebook, IconAdjustments, IconChevronDown, IconCalendar, IconClock, IconDotsVertical, IconPrinter, IconCheck, IconSquare, IconBuildingStore, IconUserCog, IconPlus, IconCirclePlus } from '@tabler/icons-react';
import { ServiceOrder, ServiceBooking, ServiceOrderBooking, Person, ServiceOrderPerson, ServiceOrderPaxGroup } from '@turizi/shared';
import { useDeleteServiceOrder, useUpdateServiceOrder } from '@/hooks/api-hooks';
import { EDIT_SERVICE_ORDER_PATH } from '@/lib/navigation';
import { useTranslation } from 'react-i18next';
import ServiceOrderInventorySelection from './service-order-inventory-selection';
import SelectServiceCreateBookingOrder from '../service-booking/select-service-available-create-booking-order';
import { useState, useMemo } from 'react';
import UpdateOrderObsModal from './update-order-obs-modal';
import EditBookingAllocationModal from './edit-booking-allocation-modal';
import ServiceBookingsList from '../service-bookings-list';
import ServiceOrderSummaryDetail from './service-order-summary-detail';
import ServiceOrderBookingsSummary from '../bookings-summary';
import SetOrderProviderPopoverItem from './set-order-provider-popover';
import SetOrderCoordinatorPopoverItem from './set-order-coordinator-popover';
import { resolveOrderBookingPaxGroups } from '@/lib/models-utils';

interface ManageServiceOrderCardProps {
    serviceOrder: ServiceOrder;
    showBookings?: boolean;
    onToggleShowBookings?: () => void;
}


export default function ManageServiceOrderCard({
    serviceOrder,
    showBookings: controlledShowBookings,
    onToggleShowBookings,
}: ManageServiceOrderCardProps): JSX.Element {
    const { t } = useTranslation();

    const [showSelectService, setShowSelectService] = useState(false);
    const [showAssignInventory, setShowAssignInventory] = useState(false);
    const [showUpdateObs, setShowUpdateObs] = useState(false);
    const [menuOpened, setMenuOpened] = useState(false);
    const [subPopoverOpened, setSubPopoverOpened] = useState(false);
    // const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [localShowBookings, setLocalShowBookings] = useState(true);

    const isShowBookingsControlled = controlledShowBookings !== undefined;
    const showBookings = isShowBookingsControlled ? controlledShowBookings : localShowBookings;

    const handleToggleShowBookings = () => {
        if (isShowBookingsControlled && onToggleShowBookings) {
            onToggleShowBookings();
        } else {
            setLocalShowBookings(!localShowBookings);
        }
    };

    // Allocation Modal State
    const [allocationOpened, setAllocationOpened] = useState(false);

    const deleteServiceOrder = useDeleteServiceOrder();
    const updateServiceOrder = useUpdateServiceOrder();

    const reservationsBookings = useMemo(() => {
        return (serviceOrder?.service_order_bookings?.map(link => {
            const b = link.service_booking;
            if (!b) return null;

            // Find which persons are assigned to this booking in this service order
            const assignedPersons = (serviceOrder.service_order_persons || [])
                .filter((sop: ServiceOrderPerson) => sop.service_booking_id === b.id)
                .map((sop: ServiceOrderPerson) => {
                    const p = sop.person_data || sop.person;
                    if (!p) return null;
                    return {
                        ...p,
                        ServiceBookingPerson: p.ServiceBookingPerson || p.service_booking_person
                    };
                })
                .filter((p): p is NonNullable<typeof p> => Boolean(p));

            // Find which pax groups are assigned to this booking in this service order
            const assignedPaxGroups = resolveOrderBookingPaxGroups(serviceOrder, b);

            const groupPersons = assignedPaxGroups.flatMap((sog: any) => sog.pax_group?.persons || []);
            const finalPersons = assignedPersons.length > 0
                ? assignedPersons
                : groupPersons;

            return {
                ...b,
                adults: link.adults,
                children: link.children,
                infants: link.infants,
                seniors: link.seniors,
                guests: link.guests,
                persons: finalPersons,
                service_order_pax_groups: assignedPaxGroups,
                _service_order_booking: link,
                _original_booking: b,
            };
        }).filter((b): b is NonNullable<typeof b> => Boolean(b))) || [];
    }, [serviceOrder]);

    const handleDelete = () => {
        deleteServiceOrder.mutate(serviceOrder.id);
    };

    return (
        <div key={serviceOrder?.id} className="w-full">
            {
                !serviceOrder
                    ? <Loader size="xs" color='black' />
                    : (
                        <div>
                            <Link
                                to={EDIT_SERVICE_ORDER_PATH.replace(':id', serviceOrder.id.toString())}
                                target="_blank"
                                className="block w-fit flex items-center gap-1.5 group mb-2 solid-box-border-hover"
                            >
                                <span className="text-md font-extrabold text-gray-700 hover:underline">
                                    <span className='font-light text-xxs'>#</span>{serviceOrder.id}
                                </span>
                            </Link>
                            <div className="w-full max-w-full flex flex-col md:flex-row items-start gap-1 md:gap-2">
                                <div>
                                    <Menu
                                        shadow="md"
                                        width={220}
                                        position="bottom-start"
                                        withinPortal
                                        opened={menuOpened}
                                        onChange={setMenuOpened}
                                        closeOnClickOutside={!subPopoverOpened}
                                    >
                                        <Menu.Target>
                                            <ActionIcon variant="subtle" color="gray" size="lg" radius="md">
                                                <IconDotsVertical size={14} stroke={1.5} />
                                            </ActionIcon>
                                        </Menu.Target>
                                        <Menu.Dropdown>
                                            <Menu.Item
                                                leftSection={<IconBusFilled size={16} className="text-amber-600" />}
                                                onClick={() => setShowAssignInventory(true)}
                                                rightSection={<IconCirclePlus size={12} stroke={2} color='gray' />}
                                            >
                                                {t('inventory')}
                                            </Menu.Item>
                                            <Menu.Item
                                                leftSection={<IconFlagCog size={16} className="text-blue-600" />}
                                                onClick={() => setShowSelectService(true)}
                                                rightSection={<IconCirclePlus size={12} stroke={2} color='gray' />}
                                            >
                                                {t('services')}
                                            </Menu.Item>
                                            <Menu.Divider />
                                            <SetOrderProviderPopoverItem
                                                order={serviceOrder}
                                                onCloseMenu={() => setMenuOpened(false)}
                                                onPopoverOpenChange={setSubPopoverOpened}
                                            />
                                            <SetOrderCoordinatorPopoverItem
                                                order={serviceOrder}
                                                onCloseMenu={() => setMenuOpened(false)}
                                                onPopoverOpenChange={setSubPopoverOpened}
                                            />
                                            <Menu.Item
                                                leftSection={<IconNotebook size={16} className="text-violet-600" />}
                                                onClick={() => setShowUpdateObs(true)}
                                            >
                                                {t('notes')}
                                            </Menu.Item>
                                            <Menu.Item
                                                leftSection={<IconAdjustments size={16} className="text-teal-600" />}
                                                rightSection={
                                                    updateServiceOrder.isPending
                                                        ? <Loader size="xs" color='black' />
                                                        : (serviceOrder.group_all_bookings !== false
                                                            ? <IconCheck size={14} />
                                                            : <IconSquare size={14} />)
                                                }
                                                disabled={updateServiceOrder.isPending}
                                                onClick={() => {
                                                    updateServiceOrder.mutate({
                                                        id: serviceOrder.id,
                                                        group_all_bookings: serviceOrder.group_all_bookings === false
                                                    });
                                                }}
                                            >
                                                {t('group_all_bookings')}
                                            </Menu.Item>
                                            <Menu.Divider />
                                            <Menu.Item
                                                component={Link}
                                                to={`/service-orders/${serviceOrder.id}/manifest`}
                                                target="_blank"
                                                leftSection={<IconPrinter size={16} />}
                                                rightSection={<IconExternalLink size={16} stroke={1} color='gray' />}
                                                className='font-bold'
                                            >
                                                {t('manifest')}
                                            </Menu.Item>
                                            <Menu.Divider />
                                            <Menu.Item
                                                color="red"
                                                leftSection={deleteServiceOrder.isPending ? (
                                                    <div className="w-4 h-4 animate-spin border-2 border-gray-300 border-t-red-600 rounded-full" />
                                                ) : (
                                                    <IconTrash size={16} />
                                                )}
                                                onClick={handleDelete}
                                                disabled={deleteServiceOrder.isPending}
                                            >
                                                {t('delete')}
                                            </Menu.Item>
                                        </Menu.Dropdown>
                                    </Menu>
                                </div>
                                {/* Selected Inventory and Ops. Services, combined table */}
                                <div className='w-full md:w-1/4 md:flex-none'>
                                    <ServiceOrderSummaryDetail order={serviceOrder} />
                                </div>
                                {/* bookings */}
                                <div className="w-full md:flex-1 min-w-0">
                                    <div className='p-2 border border-gray-200 rounded-t-xl bg-gray-50 flex gap-2 items-start md:items-center select-none group'>
                                        <div className="p-0.5 rounded hover:bg-gray-100 transition-colors">
                                            <IconChevronDown
                                                className='cursor-pointer'
                                                onClick={handleToggleShowBookings}
                                                size={16}
                                                color='gray'
                                                style={{
                                                    transform: showBookings ? 'rotate(0deg)' : 'rotate(-90deg)',
                                                    transition: 'transform 0.2s ease'
                                                }}
                                            />
                                        </div>
                                        <ServiceOrderBookingsSummary
                                            serviceOrder={serviceOrder}
                                        />
                                    </div>
                                    {showBookings && (
                                        <ServiceBookingsList
                                            showDiscrepancy={false}
                                            showHeader={false}
                                            showBookingId={true}
                                            bookings={reservationsBookings}
                                            showPaymentStatus={false}
                                            showCategory={false}
                                            showServiceOrder={false}
                                            showReservation={true}
                                            showPaxName={true}
                                            slim={true}
                                            editable={true}
                                            onPaxsClick={() => {
                                                setAllocationOpened(true);
                                            }}
                                        />
                                    )}
                                </div>
                                <ServiceOrderInventorySelection
                                    order={serviceOrder}
                                    opened={showAssignInventory}
                                    onClose={() => setShowAssignInventory(false)}
                                />
                                <SelectServiceCreateBookingOrder
                                    serviceOrder={serviceOrder}
                                    opened={showSelectService}
                                    onClose={() => setShowSelectService(false)}
                                    onCreate={(booking) => {
                                        // Optionally show a notification or rely on react-query invalidation
                                    }}
                                />
                                <UpdateOrderObsModal
                                    order={serviceOrder}
                                    opened={showUpdateObs}
                                    onClose={() => setShowUpdateObs(false)}
                                />
                                {allocationOpened && (
                                    <EditBookingAllocationModal
                                        opened={allocationOpened}
                                        onClose={() => setAllocationOpened(false)}
                                        order={serviceOrder}
                                        bookings={reservationsBookings}
                                    />
                                )}
                            </div>
                        </div>
                    )
            }
        </div>
    );
}
